import { useEffect, useState } from "react";
import { CheckIcon } from "@radix-ui/react-icons";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import {
  getFeedbackOption,
  getFeedbackOptions,
  type FeedbackEntryPoint,
  type FeedbackReasonCode,
} from "@/lib/responseFeedback";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** General feedback menu vs thumbs-down-only flow (ENG-1291). */
  entryPoint?: FeedbackEntryPoint;
  /** Saves the feedback. Rejects when it was not saved; the dialog then keeps the draft open for retry. */
  onSubmit: (feedback: { reasonCode: FeedbackReasonCode; detail: string | null }) => Promise<void>;
}

function FeedbackDialog({
  open,
  onOpenChange,
  entryPoint = "feedback",
  onSubmit,
}: FeedbackDialogProps) {
  const { toast } = useToast();
  const [selectedCode, setSelectedCode] = useState<FeedbackReasonCode | "">("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedCode("");
      setDetail("");
    }
  }, [open]);

  const selectedOption = selectedCode ? getFeedbackOption(entryPoint, selectedCode) : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCode) {
      toast({
        title: "Choose a reason",
        description: "Please select one option that best describes your feedback.",
        variant: "destructive",
      });
      return;
    }
    if (selectedCode === "other" && !detail.trim()) {
      toast({
        title: "Add a bit more detail",
        description: "Please describe your feedback when you select Other.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ reasonCode: selectedCode, detail: detail.trim() || null });
      onOpenChange(false);
      toast({
        title: "Feedback submitted",
        description: "Thank you for your feedback!",
        variant: "default",
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const HB: any = (globalThis as any).Honeybadger;
      HB && HB.notify && HB.notify(error);
      const description = error instanceof Error ? error.message : "Please try again later";
      toast({
        title: "Failed to submit feedback",
        description,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const options = getFeedbackOptions(entryPoint);

  const toggleReason = (code: FeedbackReasonCode) => {
    setSelectedCode((current) => (current === code ? "" : code));
  };

  const reasonGroupLabel =
    entryPoint === "thumbs_down" ? "Why was this response not helpful?" : "Feedback reason";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader className="pr-8">
          <DialogTitle>{entryPoint === "thumbs_down" ? "What went wrong?" : "Feedback"}</DialogTitle>
        </DialogHeader>

        <form className="grid gap-4" onSubmit={handleSubmit}>
          <p className="text-sm text-muted-foreground">
            {entryPoint === "thumbs_down"
              ? "Tell us why this response was not helpful. You can add optional detail below."
              : "Pick the option that best matches your experience. You can add optional detail below."}
          </p>

          <div className="grid gap-3" aria-label={reasonGroupLabel}>
            {options.map((opt) => {
              const selected = selectedCode === opt.code;
              return (
                <button
                  key={opt.code}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleReason(opt.code)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-3 rounded-md border border-border p-3 text-left",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "flex aspect-square h-4 w-4 shrink-0 items-center justify-center rounded-full border border-primary text-primary shadow"
                    )}
                  >
                    {selected ? <CheckIcon className="h-3.5 w-3.5 fill-primary" /> : null}
                  </span>
                  <span className="font-normal leading-snug">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {selectedOption ? (
            <div className="grid gap-2">
              <Label htmlFor="feedback-detail">{selectedOption.detailPrompt}</Label>
              <Textarea
                id="feedback-detail"
                value={detail}
                onChange={(ev) => setDetail(ev.target.value)}
                placeholder="Optional details…"
                className="min-h-[80px] resize-none"
                rows={3}
              />
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default FeedbackDialog;
